import clsx from "clsx";
import type { ReactNode } from "react";
import { Button } from "./Button";

/**
 * Los tres avisos de la sección 8.2, en un solo fichero porque comparten forma y se eligen a la
 * vez: **error** (qué falló + *Reintentar*), **vacío** (qué hacer para que deje de estarlo) y
 * **banner** (algo que la pantalla debe saber sin dejar de funcionar).
 *
 * NO SE INTERCAMBIAN
 *   Un listado vacío no es un error: no ofrece *Reintentar*, ofrece la acción que lo llena
 *   («Sincroniza para ver el mercado»). Y un error no dice qué hacer, dice qué pasó.
 */

export interface ErrorStateProps {
  /** Qué falló, en una frase corta. */
  title?: string;
  /** El detalle, si lo hay: el mensaje del servidor o la causa. */
  message?: ReactNode;
  /** Sin `onRetry` no se pinta el botón: no hay nada que reintentar. */
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = "No se han podido cargar los datos",
  message,
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      // `alert` porque el usuario no estaba esperando esto y necesita enterarse.
      role="alert"
      className={clsx(
        "grid justify-items-center gap-2.5 rounded-xl border border-dashed border-border",
        "px-3.5 py-5 text-center text-muted",
        className,
      )}
    >
      <strong className="font-semibold text-ink">{title}</strong>
      {message ? <span>{message}</span> : null}
      {onRetry ? <Button onClick={onRetry}>Reintentar</Button> : null}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  /** Qué hacer para que deje de estar vacío. */
  message?: ReactNode;
  /** La acción que lo arregla, si la pantalla puede ofrecerla. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  message,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={clsx(
        "grid justify-items-center gap-2.5 rounded-xl border border-dashed border-border",
        "px-3.5 py-5 text-center text-muted",
        className,
      )}
    >
      <strong className="font-semibold text-ink">{title}</strong>
      {message ? <span>{message}</span> : null}
      {action}
    </div>
  );
}

export type BannerTone = "warning" | "error" | "success";

const BANNER_TONES: Record<BannerTone, string> = {
  warning: "border-money/40 bg-money/[0.09]",
  error: "border-down/40 bg-down/[0.09]",
  // El acento de la sección 8.4. Se usa para confirmar algo que la persona acaba de pedir —hoy,
  // que la conexión con Biwenger responde—, nunca para adornar.
  success: "border-pitch/40 bg-pitch/[0.09]",
};

export interface BannerProps {
  tone?: BannerTone;
  title: string;
  children?: ReactNode;
  className?: string;
}

/** Aviso de pantalla: el token caducado, la falta de datos de jornada, el límite de intentos. */
export function Banner({
  tone = "warning",
  title,
  children,
  className,
}: BannerProps) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={clsx(
        "grid gap-1.5 rounded-xl border px-3 py-2.5",
        BANNER_TONES[tone],
        className,
      )}
    >
      <strong className="font-semibold text-ink">{title}</strong>
      {children ? <span className="text-muted">{children}</span> : null}
    </div>
  );
}
